import styled from 'styled-components'
import theme from 'stories/utils/theme'
import { Body1, ButtonText, Headline3 } from 'stories/elements/Typography/Text'
import _useTranslation from 'lib/hooks/_useTranslation'
import NewButton from 'components/elements/NewButton'
import VideoPlayer from 'components/modules/Players/VideoPlayer'
import Stack from 'components/elements/Stack'
import { getAIVideo } from 'lib/constants/knowunityAIUsp'
import { parseLocale } from 'lib/constants/localization'
import { useRouter } from 'next/router'
import AdvantagesContainer from './AdvantagesContainer'
import { redirectToAppDownloadUrlAsync } from 'lib/features/app-links'
import useMedia from 'lib/hooks/useMedia'
import { deviceSize } from 'lib/constants/GridBreakpoints'

const Container = styled.div`
    width: 100%;
    min-height: 800px;
    height: fit-content;
    position: relative;
    display: grid;
    .video-wrapper {
        overflow: hidden;
        background: transparent;
        border-top: 3px solid #374151;
        border-radius: 18px;
    }
    video {
        position: relative;
        border-bottom: 3px solid #374151;
        top: -25px;
    }
`

const WhatSetsUsApartComponent = () => {
    const { t: tCommon } = _useTranslation('common')
    const { t } = _useTranslation('aiUsage')
    const { locale } = useRouter()
    const parsedLocale = parseLocale(locale)
    const md = useMedia(deviceSize.md)
    const lg = useMedia(deviceSize.lg)

    const advantagesData = [
        {
            source: getAIVideo(parsedLocale.interfaceLanguageCode, 2),
            text: t('aiUsage/myAiTitle'),
            title: t('aiUsage/personaliseYourAIShorter'),
            subtext: t('aiUsage/personaliseYourAIDescription'),
            color: theme.colors.darkerGrayBackground,
            titleColor: theme.colors.businessBlue,
            textColor: theme.colors.white,
        },
        {
            source: getAIVideo(parsedLocale.interfaceLanguageCode, 3),
            text: t('aiUsage/myAiTitle'),
            title: t('aiUsage/getHelpWithYourSchoolworkWithAI'),
            subtext: t('aiUsage/getHelpWithYourWorkWithAIDescription'),
            color: theme.colors.lighterGrayBackground,
            titleColor: theme.colors.businessBlue,
            textColor: theme.colors.white,
        },
        {
            source: getAIVideo(parsedLocale.interfaceLanguageCode, 4),
            text: t('aiUsage/contentTitle'),
            title: t('aiUsage/accessMillionsOfQuizzes'),
            subtext: t('aiUsage/basedOnBestContent'),
            color: theme.colors.darkerGrayBackground,
            titleColor: theme.colors.forestGreen,
            textColor: theme.colors.white,
        },
        {
            source: getAIVideo(parsedLocale.interfaceLanguageCode, 5),
            text: t('aiUsage/tutoringTitle'),
            title: t('aiUsage/get247TutoringWithMia'),
            subtext: t('aiUsage/get247TutoringWithMiaDescription'),
            color: theme.colors.lighterGrayBackground,
            titleColor: theme.colors.signalPurple,
            textColor: theme.colors.white,
        },
        {
            source: getAIVideo(parsedLocale.interfaceLanguageCode, 6),
            text: t('aiUsage/tutoringTitle'),
            title: t('aiUsage/getPersonalisedLearningPlans'),
            subtext: t('aiUsage/getPersonalisedLearningPlansDescription'),
            color: theme.colors.darkerGrayBackground,
            titleColor: theme.colors.signalPurple,
            textColor: theme.colors.white,
        },
        {
            source: getAIVideo(parsedLocale.interfaceLanguageCode, 7),
            text: t('aiUsage/tutoringTitle'),
            title: t('aiUsage/speakToYourTutor247'),
            subtext: t('aiUsage/speakToYourTutor247Description'),
            color: theme.colors.lighterGrayBackground,
            titleColor: theme.colors.signalPurple,
            textColor: theme.colors.white,
        },
    ]

    return (
        <Container>
            {advantagesData.map((data, i) => (
                <AdvantagesContainer
                    key={i}
                    backgroundColor={data.color}
                    isReversed={i % 2 === 1}
                    noPadding={!md}
                    style={{ padding: md ? undefined : lg ? '25px' : '50px 0' }}
                >
                    <section className="content-section">
                        <div className="text-section">
                            <Stack gutter={10} className="text-section-inner">
                                <Body1
                                    className="tagline"
                                    fontFamily="var(--font-inter)"
                                    color={data.titleColor}
                                    fontSize={15}
                                    fontWeight={800}
                                >
                                    {data.text}
                                </Body1>
                                <Headline3
                                    fontFamily="var(--font-inter)"
                                    fontWeight={700}
                                    fontSize={32}
                                    style={{ color: data.textColor }}
                                >
                                    {data.title}
                                </Headline3>
                                <Body1
                                    fontFamily="var(--font-open-sans)"
                                    fontWeight={400}
                                    fontSize={16}
                                    color={'#F3F4F699'}
                                    marginBottom={20}
                                >
                                    {data.subtext}
                                </Body1>
                            </Stack>
                            <NewButton
                                className="hidden-mobile"
                                color={data.titleColor}
                                borderRadius={'100px'}
                                width={250}
                                height={60}
                                onClick={() => redirectToAppDownloadUrlAsync('dynamic', 'knowunity-ai')}
                            >
                                <ButtonText
                                    fontFamily="var(--font-inter)"
                                    fontWeight={600}
                                    fontSize={18}
                                    color={theme.colors.white}
                                >
                                    {tCommon('common/conversionCTAButton')}
                                </ButtonText>
                            </NewButton>
                        </div>
                        <div className="media-section">
                            {data.source && (
                                <VideoPlayer
                                    source={data.source}
                                    width={212}
                                    height={440}
                                    autoPlay
                                    muted
                                    loop
                                    border="3px solid #374151"
                                />
                            )}
                            <NewButton
                                className="hidden-desktop"
                                color={data.titleColor}
                                borderRadius={'100px'}
                                width={250}
                                height={60}
                                onClick={() => redirectToAppDownloadUrlAsync('dynamic', 'knowunity-ai')}
                            >
                                <ButtonText
                                    fontFamily="var(--font-inter)"
                                    fontWeight={600}
                                    fontSize={18}
                                    color={theme.colors.white}
                                >
                                    {tCommon('common/conversionCTAButton')}
                                </ButtonText>
                            </NewButton>
                        </div>
                    </section>
                </AdvantagesContainer>
            ))}
        </Container>
    )
}

export default WhatSetsUsApartComponent
